import { Controller } from "@hotwired/stimulus"
import { parseExifLocation } from "../lib/exif"
import { canvasResize, PHOTO_THUMBNAIL } from "../lib/image_resize"
import { postJson } from "../lib/http"
import { uploadThumbnailsInChunks } from "../lib/thumbnail_backfill"

// Web upload controller for the /library_photos/new page.
// Reads GPS + date from selected files via exifr, uploads metadata to
// /library_photos/batch_create, uploads thumbnails, then navigates to /discoveries.
export default class extends Controller {
  static targets = [
    "dropZone", "fileInput", "scanProgress", "progressBar",
    "progressLabel", "progressCount", "error"
  ]

  static values = {
    batchCreateUrl: String,
    thumbnailsUrl:  String,
    // 0 = public/central library; >0 = upload these photos for that community only.
    communityId:    Number,
    // Where to go after upload — the group page for a community upload, else Home.
    returnUrl:      String
  }

  connect() {
    window.photoCache = window.photoCache || {}
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  openPicker() {
    this.fileInputTarget.click()
  }

  dragover(event) {
    event.preventDefault()
    this.dropZoneTarget.style.borderColor = "#7c3aed"
    this.dropZoneTarget.style.background  = "#faf5ff"
  }

  dragleave() {
    this.dropZoneTarget.style.borderColor = "#d1d5db"
    this.dropZoneTarget.style.background  = "white"
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.style.borderColor = "#d1d5db"
    this.dropZoneTarget.style.background  = "white"
    this._process(Array.from(event.dataTransfer.files))
  }

  filesSelected() {
    this._process(Array.from(this.fileInputTarget.files))
  }

  // ── Private ──────────────────────────────────────────────────────────────

  async _process(files) {
    if (!files.length) return

    const uploadRunKey = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`

    this.dropZoneTarget.style.display   = "none"
    this.scanProgressTarget.style.display = "block"
    this.errorTarget.style.display      = "none"

    this.progressLabelTarget.textContent = "Reading photo metadata…"

    const metadata = []
    const fileByRef = {}

    for (let i = 0; i < files.length; i++) {
      this.progressBarTarget.style.width  = Math.round(((i + 1) / files.length) * 100) + "%"
      this.progressCountTarget.textContent = `${i + 1} of ${files.length}`

      const loc = await parseExifLocation(files[i])
      if (loc) {
        // Include size so distinct photos can't collide on the client_ref —
        // a collision makes the server keep the first photo's coordinates for all
        // of them (see library_photos#batch_create), collapsing matches to "0m".
        const clientRef = "web-" + files[i].name + "-" + files[i].size + "-" + files[i].lastModified
        window.photoCache[clientRef] = URL.createObjectURL(files[i])
        // Keep the File so we can generate its thumbnail + blur after the metadata
        // rows exist (the thumbnails endpoint updates rows by client_ref).
        fileByRef[clientRef] = files[i]

        metadata.push({
          latitude:   loc.latitude,
          longitude:  loc.longitude,
          taken_on:   loc.takenOn,
          taken_at:   loc.takenAt,
          client_ref: clientRef
        })
      }
    }

    if (metadata.length === 0) {
      this.scanProgressTarget.style.display = "none"
      this.dropZoneTarget.style.display     = "block"
      this.errorTarget.textContent = "None of those photos have GPS data. Try photos taken on your phone with location services on."
      this.errorTarget.style.display = "block"
      return
    }

    // Upload metadata (with thumbnails included) in batches
    this.progressLabelTarget.textContent = "Uploading…"
    this.progressBarTarget.style.width   = "0%"
    this.progressCountTarget.textContent = ""

    const BATCH = 25
    const thumbnailRefs = new Set()
    let newlyAddedCount = 0
    for (let i = 0; i < metadata.length; i += BATCH) {
      const batch = metadata.slice(i, i + BATCH)
      const response = await postJson(this.batchCreateUrlValue, { metadata: batch, community_id: this.communityIdValue })
      if (!response.ok) throw new Error(`Metadata upload failed (${response.status})`)

      const result = await response.json()
      newlyAddedCount += Number(result.created) || 0
      // Fall back to the whole batch during a rolling deploy where a client may
      // briefly receive a response from an older server without the new field.
      const needed = Array.isArray(result.thumbnail_client_refs)
        ? result.thumbnail_client_refs
        : batch.map((photo) => photo.client_ref)
      needed.forEach((ref) => thumbnailRefs.add(ref))

      this.progressBarTarget.style.width = Math.round(((i + BATCH) / metadata.length) * 100) + "%"
    }

    // Only process photos missing an image or blur. A repeated upload of an
    // already-complete library now avoids image decoding, NSFW classification,
    // direct uploads, and unattached replacement blobs.
    if (thumbnailRefs.size > 0) {
      this.progressLabelTarget.textContent = "Processing photos…"
      await uploadThumbnailsInChunks(
        Array.from(thumbnailRefs),
        (ref) => canvasResize(fileByRef[ref], PHOTO_THUMBNAIL)
      )
    }

    // All photos in — run matching once over the complete library (public →
    // global matching; a community → match only within that group). Matching
    // skips any photo the NSFW filter flagged above.
    const finalizeResponse = await postJson("/library_photos/finalize", {
      community_id: this.communityIdValue,
      upload_run_key: uploadRunKey,
      upload_photo_count: metadata.length,
      newly_added_photo_count: newlyAddedCount
    })
    if (!finalizeResponse.ok) throw new Error(`Photo matching failed (${finalizeResponse.status})`)

    const contentType = finalizeResponse.headers.get("content-type") || ""
    const result = contentType.includes("application/json") ? await finalizeResponse.json() : {}
    if (result.status === "already_up_to_date") {
      this.progressLabelTarget.textContent = "These photos have already been added. Your library is up to date."
      this.progressBarTarget.style.width = "100%"
      this.progressCountTarget.textContent = ""
      await new Promise((resolve) => setTimeout(resolve, 1800))
    }

    const destination = this.returnUrlValue && this.returnUrlValue.length > 0 ? this.returnUrlValue : "/discoveries"
    if (window.Turbo) {
      window.Turbo.visit(destination, { action: "replace" })
    } else {
      window.location.href = destination
    }
  }
}
